import { Injectable } from '@angular/core';
import { Observable, catchError, map, of, tap, throwError } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import {
	NodeChangeType,
	ApolloQueryErrorMapper,
	CombiningSubscriptionManager,
	SubscriptionManagerService,
	ApolloQueryError
} from '@shure/shared/angular/data-access/system-api/core';
import { ChannelDenoiserLevel, Response, ResponseState } from '@shure/shared/angular/data-access/system-api/models';
import { ILogger } from '@shure/shared/angular/utils/logging';
import { noMoreSubscribers } from '@shure/shared/typescript/rxjs';

import { DenoiserApiService, DenoiserChannel } from '../../api';
import {
	DenoiserChannelMutationGQL,
	DenoiserChannelQueryGQL,
	DenoiserChannelSubscriptionGQL
} from '../graphql/generated/denoiser.generated';
import { mapDenoiserChannelFromSysApi } from '../mappers';

@Injectable()
export class DaiDeviceUiDspDenoiserService implements DenoiserApiService {
	private readonly logger: ILogger;

	private readonly denoiserChannelSubscriptionManager: CombiningSubscriptionManager;

	constructor(
		logger: ILogger,
		private readonly denoiserChannelQueryGQL: DenoiserChannelQueryGQL,
		private readonly denoiserChannelMutationGQL: DenoiserChannelMutationGQL,
		denoiserChannelSubscriptionGQL: DenoiserChannelSubscriptionGQL,
		subscriptionManagerService: SubscriptionManagerService
	) {
		this.logger = logger.createScopedLogger('DaiDeviceUiDspDenoiserService');

		this.denoiserChannelSubscriptionManager = new CombiningSubscriptionManager(
			{
				name: 'DaiDeviceUiDspDenoiserService',
				changeTypes: [NodeChangeType.AudioChannelDenoiser, NodeChangeType.AudioChannelChannelConfiguration],
				subscriptionGql: denoiserChannelSubscriptionGQL
			},
			logger,
			subscriptionManagerService
		);
	}

	public getChannel$(channelId: string): Observable<DenoiserChannel> {
		const queryId = uuidv4();
		this.logger.trace('getChannel$()', 'Querying denoiser channel', { channelId, queryId });

		return this.denoiserChannelQueryGQL
			.watch({ channelId }, { errorPolicy: 'all', fetchPolicy: 'network-only' })
			.valueChanges.pipe(
				map((query) => {
					if (query.data?.node && 'features' in query.data.node) {
						this.logger.trace('getChannel$()', 'Query updated', { channelId });
						return mapDenoiserChannelFromSysApi(query.data.node);
					}

					this.logger.error('getChannel$()', 'Failed to query channel', { channelId });
					throw ApolloQueryErrorMapper.getError(query);
				}),
				tap((channel) => this.denoiserChannelSubscriptionManager.subscribe(queryId, channel.id)),
				noMoreSubscribers(() => {
					this.logger.trace('getChannel$', 'noMoreSubscribers', { channelId, queryId });
					this.denoiserChannelSubscriptionManager.unsubscribeQuery(queryId);
				}),
				catchError((error) => {
					return throwError(() => new ApolloQueryError(error));
				})
			);
	}

	public toggleDenoiser(channelId: string, enabled: boolean): Observable<Response<void, string>> {
		this.logger.trace('toggleDenoiser()', 'Toggling denoiser', { channelId, enabled });
		return this.updateChannel(channelId, { enabled });
	}

	public setDenoiserLevel(channelId: string, level: ChannelDenoiserLevel): Observable<Response<void, string>> {
		this.logger.trace('setDenoiserLevel()', 'Setting denoiser level', { channelId, level });
		return this.updateChannel(channelId, { level });
	}

	private updateChannel(
		channelId: string,
		update: Partial<DenoiserChannel['features']['denoiser']>
	): Observable<Response<void, string>> {
		return this.denoiserChannelMutationGQL
			.mutate(
				{
					updates: {
						audioChannel: {
							id: channelId,
							features: {
								denoiser: {
									...update
								}
							}
						}
					},
					injectKeyring: '//supplied-by-keyring-injector'
				},
				{ errorPolicy: 'all' }
			)
			.pipe(
				map((mutation) => {
					if (
						mutation.data?.updateNodes &&
						'error' in mutation.data.updateNodes[0] &&
						mutation.data.updateNodes[0].error !== null
					) {
						this.logger.error('updateChannel()', 'Failed to update channel', {
							channelId,
							mutation
						});
						return { state: ResponseState.Error, error: mutation.data.updateNodes[0].error?.message };
					}

					this.logger.trace('updateChannel()', 'Done', { channelId });
					return { state: ResponseState.Done };
				}),
				catchError((error) => {
					this.logger.error('updateChannel()', 'Failed to update channel', { error });
					return of({ state: ResponseState.Error, error });
				})
			);
	}
}
